import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../utils/toast';
import './AdminTable.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.privemobility.com/api';

const AdditionalsAdmin = () => {
  const [additionals, setAdditionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAdditional, setEditingAdditional] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadAdditionals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/additionals`);
      setAdditionals(response.data || []);
    } catch (error) {
      console.error('Error loading additionals:', error);
      toast.error('Error al cargar adicionales');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdditionals();
  }, [loadAdditionals]);

  const handleEdit = (additional) => {
    setEditingAdditional(additional);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingAdditional(null);
    setShowForm(true);
  };

  const handleSave = async (additionalData) => {
    try {
      await axios.post(`${API_URL}/additionals`, additionalData);
      toast.success(editingAdditional ? 'Adicional actualizado exitosamente' : 'Adicional creado exitosamente');
      setShowForm(false);
      setEditingAdditional(null);
      loadAdditionals();
    } catch (error) {
      console.error('Error saving additional:', error);
      toast.error('Error al guardar adicional');
    }
  };

  if (loading) {
    return <div className="admin-loading">Cargando adicionales...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gestión de Servicios Adicionales</h2>
        <button className="btn-primary" onClick={handleCreate}>
          + Nuevo Adicional
        </button>
      </div>

      {showForm && (
        <AdditionalForm
          additional={editingAdditional}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingAdditional(null);
          }}
        />
      )}

      <div className="additionals-grid">
        {additionals.map(additional => (
          <div key={additional.id} className="additional-card">
            <div className="additional-icon">{additional.icon}</div>
            <h3>{additional.name}</h3>
            <p className="additional-description">{additional.description}</p>
            <div className="additional-price">
              $ {additional.pricePerDay?.toLocaleString('es-AR')} / día
            </div>
            <div className="additional-status">
              <span className={`status ${additional.active ? 'active' : 'inactive'}`}>
                {additional.active ? 'Activo' : 'Inactivo'}
              </span>
              {additional.required && (
                <span className="badge-required">Requerido</span>
              )}
            </div>
            <div className="action-buttons">
              <button className="btn-edit" onClick={() => handleEdit(additional)}>
                ✏️ Editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {additionals.length === 0 && (
        <div className="empty-state">
          No hay servicios adicionales registrados. Crea uno nuevo para comenzar.
        </div>
      )}
    </div>
  );
};

const AdditionalForm = ({ additional, onSave, onCancel }) => {
  const [formData, setFormData] = useState(additional || {
    id: '',
    name: '',
    description: '',
    pricePerDay: 0,
    icon: '📦',
    active: true,
    required: false,
    maxQuantity: 1
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const iconOptions = [
    { value: '👤', label: '👤 Persona' },
    { value: '👥', label: '👥 Personas' },
    { value: '✈️', label: '✈️ Aeropuerto' },
    { value: '👶', label: '👶 Bebé' },
    { value: '🪑', label: '🪑 Silla' },
    { value: '⛓️', label: '⛓️ Cadenas' },
    { value: '📍', label: '📍 Ubicación' },
    { value: '🚗', label: '🚗 Auto' }
  ];

  return (
    <div className="form-modal">
      <div className="form-content">
        <h3>{additional ? 'Editar Adicional' : 'Nuevo Adicional'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>ID *</label>
              <input
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                required
                disabled={!!additional}
              />
            </div>

            <div className="form-group">
              <label>Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group full-width">
              <label>Descripción *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Precio por Día *</label>
              <input
                type="number"
                step="0.01"
                value={formData.pricePerDay}
                onChange={(e) => setFormData({ ...formData, pricePerDay: parseFloat(e.target.value) })}
                required
              />
            </div>

            <div className="form-group">
              <label>Cantidad Máxima *</label>
              <input
                type="number"
                min="1"
                value={formData.maxQuantity}
                onChange={(e) => setFormData({ ...formData, maxQuantity: parseInt(e.target.value) || 1 })}
                required
              />
              <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                Cantidad máxima que se puede seleccionar (por defecto: 1)
              </small>
            </div>

            <div className="form-group">
              <label>Icono</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {iconOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span>Activo</span>
              </label>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.required}
                  onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                />
                <span>Requerido</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdditionalsAdmin;
